// Player pre-game customization layout: title, avatar, color carousel, join controls slot.

import React from 'react';
import ColorCarousel from './ColorCarousel';
import Avatar from '@/components/avatars/Avatar';
import AvatarSideRails from '@/components/avatars/AvatarSideRails';
import { renderAnimalHappySwatch } from '@/components/avatars/utilities/animalAvatarAssets';
import PlayerJoinControls from './PlayerJoinControls';
import { useAnimalStyleAvatars } from '@/components/avatars/hooks';
import { useRoomMyPlayer } from '@/hooks/useRoomMyPlayer';
import type { PlayerColorFamily } from '@/styles/playerColors';

interface PlayerCustomizationPanelProps {
  avatarSize: number;
  availableColors: readonly PlayerColorFamily[];
  selectedColor: PlayerColorFamily;
  onColorSelect: (color: PlayerColorFamily) => void;
  customFace: { eyeShape: string; mouthShape: string };
  onCycleExpression: (
    feature: 'eyes' | 'mouth',
    direction: 'prev' | 'next'
  ) => void;
  isJoining: boolean;
  onSubmit: (playerName: string) => Promise<void | false>;
  onTwitchAuthClick: () => void;
}

const PlayerCustomizationPanel: React.FC<PlayerCustomizationPanelProps> = ({
  avatarSize,
  availableColors,
  selectedColor,
  onColorSelect,
  customFace,
  onCycleExpression,
  isJoining,
  onSubmit,
  onTwitchAuthClick,
}) => {
  const isAnimalStyleRoom = useAnimalStyleAvatars();
  const myPlayer = useRoomMyPlayer();

  return (
    <div className="flex w-full max-w-full flex-col items-center justify-center gap-1.5 overflow-x-hidden sm:gap-2 md:gap-3">
      <h1 className="text-2xl font-bold text-foreground sm:text-3xl md:text-4xl">
        {isAnimalStyleRoom ? 'Pick Your Animal' : 'Customize Your Game Face'}
      </h1>

      <div className="-my-[6%] relative flex items-center justify-center sm:-my-[5%] md:-my-[4%]">
        <div className="flex items-center justify-center">
          <Avatar
            player={
              myPlayer
                ? {
                    ...myPlayer,
                    playerColor: selectedColor || myPlayer.playerColor,
                  }
                : undefined
            }
            emotion="happy"
            customExpression={customFace}
            animalStyle={isAnimalStyleRoom}
            size={avatarSize}
          />
        </div>
        {!isAnimalStyleRoom && (
          <AvatarSideRails
            onCycleEyes={(direction) => onCycleExpression('eyes', direction)}
            onCycleMouth={(direction) => onCycleExpression('mouth', direction)}
          />
        )}
      </div>

      <ColorCarousel
        colors={availableColors}
        selectedColor={selectedColor}
        onColorSelect={onColorSelect}
        className="my-0 max-w-[380px]"
        trackClassName="h-16 py-0"
        renderSwatch={isAnimalStyleRoom ? renderAnimalHappySwatch : undefined}
      />

      <div className="flex w-full max-w-sm flex-col items-center gap-3 sm:max-w-md">
        <PlayerJoinControls
          isJoining={isJoining}
          onSubmit={onSubmit}
          onTwitchAuthClick={onTwitchAuthClick}
        />
      </div>
    </div>
  );
};

export default PlayerCustomizationPanel;
